/**
 * Helper (pure) ของกราฟใน MeGen Cost — ส่วนที่ "คิด" ไม่ใช่ส่วนที่ประกาศ spec
 *
 * spec ของ Vega-Lite ยังอยู่ใน CardMeGenCostMain.tsx เพราะเป็น config เชิงประกาศที่ผูกกับ state ของหน้าจอ
 * (ย้ายออกมาแล้วต้องส่ง argument สิบกว่าตัว และเสี่ยงกราฟเปลี่ยนโดยไม่มีเทสต์จับ)
 * ที่นี่เก็บเฉพาะกฎที่พลาดง่ายและเทสต์ได้: การจัดข้อความค่า metric และตาราง tooltip ของแท่ง stack
 */
import { AiUsageMetricKey, formatMetricValue, metricUnitOf } from "./aiUsagePivot";

/**
 * ค่า metric สำหรับ tooltip — ต่อหน่วยท้ายค่า **ยกเว้น `cost_usd`**
 * (cost ถูก format เป็นเงินอยู่แล้ว ถ้าต่อหน่วยจะได้ "$1.23 USD")
 */
export const formatMetricWithUnit = (key: AiUsageMetricKey, value: number): string =>
  key === "cost_usd" ? formatMetricValue(key, value) : `${formatMetricValue(key, value)} ${metricUnitOf(key)}`;

/** สัดส่วนเป็น % — ตั้งแต่ 10% ขึ้นไปปัดเป็นจำนวนเต็ม ต่ำกว่านั้นเอาทศนิยม 1 ตำแหน่ง */
export const sharePercentLabel = (value: number, total: number): string => {
  const p = total > 0 ? (value / total) * 100 : 0;
  return p >= 10 ? `${Math.round(p)}%` : `${p.toFixed(1)}%`;
};

export type StackRow = { day: string; group: string; value: number };

/**
 * ตาราง tooltip ของแท่ง stack: 1 object ต่อ 1 วัน (vega-tooltip render object เป็นตาราง)
 * เรียงกลุ่มจากมากไปน้อย และใส่ยอดรวมของทั้งแท่งไว้บนสุด
 */
export const buildStackHover = (
  rows: StackRow[],
  metric: AiUsageMetricKey,
  labels: { day: string; total: string } = { day: "วันที่", total: "รวมทั้งแท่ง" }
): Record<string, Record<string, string>> => {
  const byDay: Record<string, Record<string, string>> = {};
  const grouped = new Map<string, StackRow[]>();
  rows.forEach((row) => {
    const group = grouped.get(row.day);
    if (group) group.push(row); else grouped.set(row.day, [row]);
  });
  grouped.forEach((group, day) => {
    const dayRows = group.sort((a, b) => b.value - a.value);
    const total = dayRows.reduce((sum, x) => sum + x.value, 0);
    const hover: Record<string, string> = {
      [labels.day]: day,
      [labels.total]: formatMetricWithUnit(metric, total),
    };
    dayRows.forEach((x) => {
      hover[x.group] = `${formatMetricWithUnit(metric, x.value)} (${sharePercentLabel(x.value, total)})`;
    });
    byDay[day] = hover;
  });
  return byDay;
};
